import { Component } from "@theme/component";

/**
 * Search dropdown component that manages the inline search dropdown
 * Similar to the mega menu but for search functionality
 *
 * @typedef {object} Refs
 * @property {HTMLElement} searchToggle - The search form container
 * @property {HTMLInputElement} headerInput - The header search input
 * @property {HTMLElement} searchDropdown - The dropdown container
 * @extends {Component<Refs>}
 */
class SearchDropdown extends Component {
    requiredRefs = ["searchToggle", "headerInput", "searchDropdown"];

    #abortController = new AbortController();
    #isOpen = false;
    #predictiveSearchInput = null;
    #predictiveSearchComponent = null;
    #headerResetButton = null;

    connectedCallback() {
        super.connectedCallback();

        // Get the predictive search input inside the dropdown
        this.#predictiveSearchInput = this.querySelector(".search-dropdown__predictive .search-input");
        this.#predictiveSearchComponent = this.querySelector("predictive-search-component");
        this.#headerResetButton = this.refs.headerResetButton;

        // Listen to header input events
        this.refs.headerInput.addEventListener("input", this.#handleHeaderInput, {
            signal: this.#abortController.signal,
        });

        this.refs.headerInput.addEventListener("focus", this.#handleHeaderFocus, {
            signal: this.#abortController.signal,
        });

        // Submit to search page on Enter
        this.refs.headerInput.addEventListener("keydown", this.#handleHeaderKeyDown, {
            signal: this.#abortController.signal,
        });

        // Listen to header reset button
        if (this.#headerResetButton) {
            this.#headerResetButton.addEventListener("click", this.#handleHeaderReset, {
                signal: this.#abortController.signal,
            });
        }

        // Close dropdown when clicking outside
        document.addEventListener("click", this.#handleClickOutside, {
            signal: this.#abortController.signal,
        });

        // Close dropdown on escape key
        document.addEventListener("keydown", this.#handleEscape, {
            signal: this.#abortController.signal,
        });

        // Listen for close button clicks inside the predictive search
        const closeButton = this.querySelector(".predictive-search__close-modal-button");
        if (closeButton) {
            closeButton.addEventListener("click", () => this.closeSearch(), {
                signal: this.#abortController.signal,
            });
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.#abortController.abort();
    }

    /**
     * Handle input in the header search field
     */
    #handleHeaderInput = (event) => {
        const value = event.target.value;

        // Show/hide reset button based on input value
        if (this.#headerResetButton) {
            this.#headerResetButton.hidden = value.trim().length === 0;
        }

        // Sync the value to the predictive search input
        if (this.#predictiveSearchInput) {
            this.#predictiveSearchInput.value = value;

            // Trigger the input event on the predictive search input
            // Use InputEvent with inputType property so predictive search recognizes it
            const inputEvent = new InputEvent("input", {
                bubbles: true,
                inputType: "insertText",
                data: value,
            });
            this.#predictiveSearchInput.dispatchEvent(inputEvent);
        } // Open dropdown if there's text and it's not already open
        if (value.trim().length > 0 && !this.#isOpen) {
            this.openSearch();
        }
    };

    /**
     * Handle Enter key on the header input to navigate to search page
     */
    #handleHeaderKeyDown = (event) => {
        if (event.key !== "Enter") return;

        event.preventDefault();
        const term = this.refs.headerInput.value;

        // Prefer theme route if available, fallback to /search
        const base = window.Theme && Theme.routes && Theme.routes.search_url ? Theme.routes.search_url : "/search";
        const url = new URL(base, location.origin);
        url.searchParams.set("q", term);

        window.location.href = url.toString();
    };

    /**
     * Handle reset button click
     */
    #handleHeaderReset = () => {
        // Clear the header input
        this.refs.headerInput.value = "";

        // Hide the reset button
        if (this.#headerResetButton) {
            this.#headerResetButton.hidden = true;
        }

        // Clear the predictive search input
        if (this.#predictiveSearchInput) {
            this.#predictiveSearchInput.value = "";
        }

        // Close the dropdown
        this.closeSearch();

        // Focus back on the input
        this.refs.headerInput.focus();
    };

    /**
     * Handle focus on the header input
     */
    #handleHeaderFocus = () => {
        // Always open on focus to show default lists (recent/trending)
        if (!this.#isOpen) {
            this.openSearch();
        }

        // When focusing with empty value, render empty state lists
        if (this.refs.headerInput.value.trim().length === 0 && this.#predictiveSearchComponent) {
            // Render empty state (recently viewed + trending) without moving focus
            if (typeof this.#predictiveSearchComponent.resetSearch === "function") {
                this.#predictiveSearchComponent.resetSearch(false);
            }
        }
    };

    /**
     * Open the search dropdown
     */
    openSearch() {
        if (this.#isOpen) return;

        this.#isOpen = true;
        this.setAttribute("aria-expanded", "true");
        this.refs.searchDropdown.hidden = false;

        // Sync values when opening
        if (this.#predictiveSearchInput && this.refs.headerInput.value) {
            this.#predictiveSearchInput.value = this.refs.headerInput.value;
        }

        // If opening with empty query, ensure empty state is visible immediately
        if (this.refs.headerInput.value.trim() === "" && this.#predictiveSearchComponent) {
            if (typeof this.#predictiveSearchComponent.resetSearch === "function") {
                this.#predictiveSearchComponent.resetSearch(false);
            }
        }
    }

    /**
     * Close the search dropdown
     */
    closeSearch() {
        if (!this.#isOpen) return;

        this.#isOpen = false;
        this.setAttribute("aria-expanded", "false");

        // Clear the header input
        this.refs.headerInput.value = "";

        // Clear the predictive search input
        if (this.#predictiveSearchInput) {
            this.#predictiveSearchInput.value = "";
        }

        // Reset the predictive search component
        if (this.#predictiveSearchComponent && typeof this.#predictiveSearchComponent.resetSearch === "function") {
            this.#predictiveSearchComponent.resetSearch(false);
        }

        // Wait for transition before hiding
        setTimeout(() => {
            this.refs.searchDropdown.hidden = true;
        }, 250);
    }

    /**
     * Handle clicks outside the dropdown
     */
    #handleClickOutside = (event) => {
        if (!this.#isOpen) return;

        const target = event.target;

        // Don't close if clicking inside the dropdown or the search form
        if (this.contains(target)) return;

        this.closeSearch();
    };

    /**
     * Handle escape key press
     */
    #handleEscape = (event) => {
        if (event.key === "Escape" && this.#isOpen) {
            this.closeSearch();
            this.refs.headerInput.blur();
        }
    };
}
if (!customElements.get("search-dropdown")) {
    customElements.define("search-dropdown", SearchDropdown);
}
